/**
 * The format-neutral report model (UX-SPEC §10, UX-EXP-02) built from a ViewModel, and the export file names
 * (UX-EXP-04). The renderers (`report-md.ts`, `report-txt.ts`, `report-html.ts`, `report-docx.ts`) are pure functions of
 * this model. The document text is never part of it.
 *
 * Every string is single-line (control characters and line breaks become spaces) so that no renderer needs to reason
 * about them; 10,000+ hotspot rows stay cheap because rows are plain string arrays built in one pass.
 */
import { allExplainers } from '../engine/channels/explain.ts';
import { CHANNEL_IDS } from '../engine/channels/types.ts';
import { diagnosticsSections, hygieneLine, thousands } from '../shared/diagnostics.ts';
import type { DiagSection } from '../shared/diagnostics.ts';
import { dialTextLines } from '../shared/dial.ts';
import { figuresFor } from '../shared/model-figures.ts';
import type { ChannelDetail, ViewModel } from '../shared/viewmodel.ts';
import {
	DIRECTION,
	HEADLINE_EXPLAIN,
	STATUS_EXPLAIN,
	shortPointSentence,
	unreachableSentence,
} from '../web/strings-explain.ts';
import { glossaryEntries } from '../web/strings-glossary.ts';

export type Align = 'l' | 'r';

export type Block =
	| { t: 'p'; text: string; bold?: boolean }
	| { t: 'h'; text: string }
	| { t: 'list'; items: string[] }
	| { t: 'kv'; rows: Array<[string, string]> }
	| { t: 'table'; head: string[]; rows: string[][]; align: Align[] };

export interface ReportSection {
	id: string;
	title: string;
	blocks: Block[];
}

export interface Report {
	title: string;
	/** The source facts (name, format, words, analysed at, engine version, model id, calibration state). */
	source: Array<[string, string]>;
	sections: ReportSection[];
}

/* ---------------- file names (UX-EXP-04) ---------------- */

export type ExportFormatKey = 'json' | 'md' | 'txt' | 'html' | 'docx';

const EXTENSIONS: Record<ExportFormatKey, string> = {
	json: '.segments.json',
	md: '.provenance.md',
	txt: '.provenance.txt',
	html: '.provenance.html',
	docx: '.provenance.docx',
};

const NAME_CAP = 80;
const SOURCE_EXT = /\.(txt|md|markdown|html?|docx?|pdf|rtf|odt)$/i;

function hostOf(url: string): string | null {
	try {
		return new URL(url).host || null;
	} catch {
		return null;
	}
}

/** The source name an export file is named after. */
export function baseName(meta: ViewModel['meta']): string {
	let raw: string;
	if (meta.sourceKind === 'paste') raw = 'pasted-text';
	else if (meta.sourceKind === 'url')
		raw = (meta.finalUrl && hostOf(meta.finalUrl)) || hostOf(meta.name) || meta.name;
	else {
		// strip a source extension first ('the-book.docx' → 'the-book')
		raw = meta.name.replace(SOURCE_EXT, '');
	}
	let safe = raw.replace(/[^A-Za-z0-9._ -]/g, '_').slice(0, NAME_CAP);
	safe = safe.replace(/^[ .]+|[ .]+$/g, '');
	return safe === '' ? 'provenance' : safe;
}

export function exportFilename(meta: ViewModel['meta'], format: ExportFormatKey): string {
	return `${baseName(meta)}${EXTENSIONS[format]}`;
}

/* ---------------- text helpers ---------------- */

const CONTROL = new RegExp(
	[
		'[\\u0000-\\u001F\\u007F-\\u009F\\u2028\\u2029\\uFFFE\\uFFFF]',
		'[\\uD800-\\uDBFF](?![\\uDC00-\\uDFFF])',
		'(?<![\\uD800-\\uDBFF])[\\uDC00-\\uDFFF]',
	].join('|'),
	'g'
);

/** Single-line, XML-safe text. */
export function clean(s: string): string {
	return s.replace(CONTROL, ' ');
}

const f1 = (x: number): string => x.toFixed(1);
const f2 = (x: number): string => x.toFixed(2);
const interval = (ci: readonly [number, number] | undefined): string =>
	ci ? `${f1(ci[0])}–${f1(ci[1])}` : '-';
const signed1 = (x: number): string => `${x >= 0 ? '+' : ''}${x.toFixed(1)}`;
const pValue = (p: number | undefined): string =>
	p === undefined ? '-' : p < 0.001 ? p.toExponential(1) : p.toFixed(3);
const ratio = (x: number | undefined): string => (x === undefined ? '-' : x.toFixed(2));
const paraRange = (a: number, b: number): string =>
	b - a <= 1 ? `paragraph ${a + 1}` : `paragraphs ${a + 1}–${b}`;

/** Hotspot entries whose first paragraph lies in [a, b), from a prefix-sum over paragraphs. */
function makeCounter(vm: ViewModel): (a: number, b: number) => number {
	const n = vm.paras.count;
	const prefix = new Uint32Array(n + 2);
	for (const h of vm.ensemble.hotspots.hotspots) {
		const p = Math.min(Math.max(h.paraStart, 0), n);
		prefix[p + 1] = (prefix[p + 1] as number) + 1;
	}
	for (let i = 1; i < prefix.length; i++)
		prefix[i] = (prefix[i] as number) + (prefix[i - 1] as number);
	return (a, b) => {
		const lo = Math.min(Math.max(a, 0), n);
		const hi = Math.min(Math.max(b, lo), n);
		return (prefix[hi] as number) - (prefix[lo] as number);
	};
}

const STRIP_CAPTION =
	'Index per section that shares heuristic characteristics (joint segments). Each section is aggregated on its own text, so sections need not average to the document index.';

const HOTSPOT_DEFINITIONS =
	'Hotspot: significantly above both the human reference and the document’s own background. Elevated: above the reference. Kind: segment (a run of paragraphs between change points), single paragraph, chapter.';

/* ---------------- sections ---------------- */

function sourceRows(vm: ViewModel): Array<[string, string]> {
	const { meta, ensemble: ens } = vm;
	const rows: Array<[string, string]> = [
		['Name', meta.name],
		['Source', meta.sourceKind],
		[
			'Format',
			meta.dispatchedFormat ? `${meta.format} (read as ${meta.dispatchedFormat})` : meta.format,
		],
		['Words', thousands(meta.words)],
	];
	if (meta.finalUrl) rows.push(['Final URL', meta.finalUrl]);
	rows.push(
		['Analysed at', meta.analysedAt],
		['Engine version', meta.engineVersion],
		['Model', `${meta.model.id} (${ens.model.calibrated ? 'calibrated' : 'uncalibrated'})`],
		['Document hash', meta.docHash]
	);
	return rows;
}

function patternsSection(vm: ViewModel): ReportSection {
	const ens = vm.ensemble;
	const list = [...ens.patterns];
	if (ens.translation.pattern && !list.some((p) => p.kind === ens.translation.pattern?.kind))
		list.push(ens.translation.pattern);
	const blocks: Block[] = [];
	if (list.length === 0) blocks.push({ t: 'p', text: 'No provenance pattern indicators fired.' });
	else {
		blocks.push({
			t: 'p',
			text: 'Indicator (a rule output with the evidence that triggered it):',
		});
		for (const p of list) {
			blocks.push({ t: 'p', text: p.value ? `${p.name} (${p.value})` : p.name, bold: true });
			blocks.push({ t: 'list', items: [...p.triggers] });
			if (p.kind === 'assisted')
				blocks.push({ t: 'p', text: 'The index is floored by this rule.' });
		}
	}
	return { id: 'patterns', title: 'Provenance patterns', blocks };
}

function noticesSection(vm: ViewModel): ReportSection {
	const { meta, ensemble: ens } = vm;
	const items: string[] = [];
	const withheld = new Map<string, string>();
	for (const w of ens.hotspots.withheld) withheld.set(w.id, w.reason);
	for (const c of ens.channels)
		if (c.flagsWithheld && !withheld.has(c.id)) withheld.set(c.id, c.flagsWithheld);
	if (withheld.size) {
		items.push(
			`Hotspot flags withheld for ${[...withheld.keys()].join(', ')} until the channel priors are replaced by corpus values.`
		);
		const reasons = new Map<string, string[]>();
		for (const [id, r] of withheld) reasons.set(r, [...(reasons.get(r) ?? []), id]);
		for (const [r, ids] of reasons) items.push(`Reason (${ids.join(', ')}): ${r}`);
	}
	const ld = ens.diagnostics.lengthDependent;
	if (ld.length)
		items.push(
			`${ld.join(' and ')} ${ld.length === 1 ? 'is' : 'are'} length-dependent until the channel priors are replaced by corpus values.`
		);
	if (ens.abstained.length)
		items.push(
			`${ens.abstained.length} ${ens.abstained.length === 1 ? 'channel' : 'channels'} abstained: ${ens.abstained.map((a) => a.id).join(', ')}.`
		);
	for (const w of meta.warnings) items.push(`Intake warning: ${w}`);
	const hygiene = hygieneLine(ens.hygiene);
	if (hygiene) items.push(hygiene);
	if (!ens.model.calibrated) items.push(`Uncalibrated model: ${ens.model.id}`);
	if (ens.calibratedIndex?.reason)
		items.push(`Calibrated index not available: ${ens.calibratedIndex.reason}.`);
	const ci = ens.calibratedIndex;
	if (ci?.interval === null && ci.intervalReason && !ci.reason)
		items.push(`Text-sampling interval not available: ${ci.intervalReason}.`);
	if (ci?.status === 'abstain-interval') items.push(`Calibrated index ${ci.statusPhrase}.`);
	return {
		id: 'notices',
		title: 'Notices',
		blocks: [items.length ? { t: 'list', items } : { t: 'p', text: 'No notices.' }],
	};
}

function channelRow(c: ChannelDetail, vm: ViewModel, hotspotCount: Map<string, number>): string[] {
	const row = vm.ensemble.channels.find((r) => r.id === c.id);
	const notes = [...(row?.notes ?? c.notes)];
	if (row?.referenceNote) notes.push(row.referenceNote);
	if (c.status === 'abstain' || !row || row.score === undefined) {
		return [
			c.id,
			c.name,
			c.family,
			c.axis,
			'-',
			'-',
			'-',
			'-',
			c.flagsWithheld ? 'flags withheld' : '-',
			[`Abstained: ${c.reason ?? row?.reason ?? 'abstained'}`, ...notes].join('; '),
		];
	}
	const kappa = row.kappa ?? 0;
	return [
		c.id,
		c.name,
		c.family,
		c.axis,
		f1(row.score),
		interval(row.ci90),
		kappa < 0.2 ? `${f2(kappa)} (little evidence)` : f2(kappa),
		row.deviation === undefined ? '-' : signed1(row.deviation),
		c.flagsWithheld ? 'flags withheld' : String(hotspotCount.get(c.id) ?? 0),
		notes.join('; '),
	];
}

function channelsSection(vm: ViewModel): ReportSection {
	const counts = new Map<string, number>();
	for (const h of vm.ensemble.hotspots.hotspots)
		counts.set(h.channel, (counts.get(h.channel) ?? 0) + 1);
	return {
		id: 'channels',
		title: 'Channels',
		blocks: [
			{
				t: 'p',
				text: `All ${CHANNEL_IDS.length} channels, reported separately. Evidence strength runs from 0 to 100; deviation is measured from the reference (the prior until C1/C3); data weight κ runs from 0 to 1.`,
			},
			{
				t: 'table',
				head: [
					'ID',
					'Name',
					'Family',
					'Axis',
					'Evidence strength',
					'90% interval',
					'Data weight κ',
					'Deviation from the reference',
					'Hotspot entries',
					'Notes',
				],
				rows: vm.channels.map((c) => channelRow(c, vm, counts)),
				align: ['l', 'l', 'l', 'l', 'r', 'r', 'r', 'r', 'r', 'l'],
			},
		],
	};
}

function segmentTable(
	list: ViewModel['ensemble']['segments']['joint'],
	count: (a: number, b: number) => number,
	titled: boolean
): Block {
	const head = titled
		? ['#', 'Title', 'Words', 'Index', '90% interval', 'Interval method', 'Hotspot entries inside']
		: [
				'#',
				'Paragraphs',
				'Words',
				'Index',
				'90% interval',
				'Interval method',
				'Hotspot entries inside',
			];
	return {
		t: 'table',
		head,
		rows: list.map((s) => [
			String(s.ordinal + 1),
			titled
				? (s.title ?? `${s.kind === 'scene' ? 'Scene' : 'Chapter'} ${s.ordinal + 1}`)
				: `${s.range.paraStart + 1}–${s.range.paraEnd}`,
			thousands(s.words),
			f1(s.index),
			interval(s.ci90),
			s.ciMethod,
			String(count(s.range.paraStart, s.range.paraEnd)),
		]),
		align: ['r', 'l', 'r', 'r', 'r', 'l', 'r'],
	};
}

function jointSection(vm: ViewModel, count: (a: number, b: number) => number): ReportSection {
	return {
		id: 'segments',
		title: 'Joint segments',
		blocks: [
			{ t: 'p', text: STRIP_CAPTION },
			segmentTable(vm.ensemble.segments.joint, count, false),
		],
	};
}

function unitsSection(vm: ViewModel, count: (a: number, b: number) => number): ReportSection {
	const units = vm.ensemble.segments.units;
	const kind = units[0]?.kind === 'scene' ? 'Scenes' : 'Chapters';
	return {
		id: 'chapters',
		title: kind,
		blocks: units.length
			? [segmentTable(units, count, true)]
			: [{ t: 'p', text: 'No chapters or scenes were found in this document.' }],
	};
}

function hotspotsSection(vm: ViewModel): ReportSection {
	const { hotspots, withheld } = vm.ensemble.hotspots;
	const blocks: Block[] = [];
	if (withheld.length) {
		blocks.push({
			t: 'p',
			text: `Not listed: ${withheld.map((w) => w.id).join(', ')}. ${withheld[0]?.reason ?? ''}`.trim(),
		});
	}
	let hs = 0;
	let es = 0;
	let eu = 0;
	let sp = 0;
	for (const h of hotspots) {
		if (h.kind === 'segment') h.flag === 'hotspot' ? hs++ : es++;
		else if (h.kind === 'unit') eu++;
		else sp++;
	}
	const parts = [
		hs ? `${thousands(hs)} hotspot ${hs === 1 ? 'segment' : 'segments'}` : '',
		es ? `${thousands(es)} elevated ${es === 1 ? 'segment' : 'segments'}` : '',
		eu ? `${thousands(eu)} elevated ${eu === 1 ? 'chapter' : 'chapters'}` : '',
		sp ? `${thousands(sp)} elevated single ${sp === 1 ? 'paragraph' : 'paragraphs'}` : '',
	].filter(Boolean);
	if (hotspots.length === 0) {
		blocks.push({ t: 'p', text: 'No hotspot entries.' });
		return { id: 'hotspots', title: 'Hotspots', blocks };
	}
	blocks.push({
		t: 'p',
		text: `${thousands(hotspots.length)} ${hotspots.length === 1 ? 'entry' : 'entries'}: ${parts.join(', ')}.`,
	});
	blocks.push({ t: 'p', text: HOTSPOT_DEFINITIONS });

	// chapter titles repeat across neighbouring rows: cache per chapter index
	const titleCache = new Map<number, string>();
	const chapterTitle = (para: number): string => {
		const ci = vm.paras.chapter[Math.min(Math.max(para, 0), Math.max(0, vm.paras.count - 1))] ?? -1;
		let t = titleCache.get(ci);
		if (t === undefined) {
			const ch = ci >= 0 ? vm.chapters[ci] : undefined;
			t = ch ? (ch.title ?? `Chapter ${ci + 1}`) : '';
			titleCache.set(ci, t);
		}
		return t;
	};
	const rows: string[][] = new Array(hotspots.length);
	for (let i = 0; i < hotspots.length; i++) {
		const h = hotspots[i];
		if (!h) continue;
		const where = paraRange(h.paraStart, h.paraEnd);
		const ct = chapterTitle(h.paraStart);
		rows[i] = [
			String(i + 1),
			h.channel,
			h.name,
			h.kind,
			h.flag,
			ct ? `${ct}, ${where}` : where,
			f1(h.score),
			interval(h.ci90),
			ratio(h.rateRatioRef),
			ratio(h.rateRatioBg),
			pValue(h.pRef),
			pValue(h.pBg),
			h.notes ? h.notes.join('; ') : '',
		];
	}
	blocks.push({
		t: 'table',
		head: [
			'Rank',
			'Channel',
			'Name',
			'Kind',
			'Flag',
			'Location',
			'Evidence strength',
			'Interval (ramp units)',
			'Rate ratio to the reference',
			'Rate ratio to the background',
			'pRef',
			'pBg',
			'Notes',
		],
		rows,
		align: ['r', 'l', 'l', 'l', 'l', 'l', 'r', 'r', 'r', 'r', 'r', 'r', 'l'],
	});
	return { id: 'hotspots', title: 'Hotspots', blocks };
}

function diagBlocks(sections: DiagSection[]): Block[] {
	const blocks: Block[] = [];
	for (const s of sections) {
		blocks.push({ t: 'h', text: s.title });
		if (s.rows?.length) blocks.push({ t: 'kv', rows: s.rows });
		if (s.table) {
			if (s.table.rows.length)
				blocks.push({
					t: 'table',
					head: s.table.head,
					rows: s.table.rows,
					align: s.table.head.map(() => 'l' as Align),
				});
			else blocks.push({ t: 'p', text: 'None.' });
		}
		if (s.lines?.length) {
			// single lines as paragraphs; several as a list
			if (s.lines.length === 1) blocks.push({ t: 'p', text: s.lines[0] as string });
			else blocks.push({ t: 'list', items: s.lines });
		}
	}
	return blocks;
}

/**
 * P15 (UD-18): the plain-language reading of the headline, added after the headline lines. The status sentence comes
 * from the engine's `status` (the report never compares a value to the reference point); the reconciliation note is
 * added only when the calibrated index and the seed-rules composite sit on opposite sides of 50.
 */
export function headlineHelpBlocks(vm: ViewModel): Block[] {
	const ens = vm.ensemble;
	const ci = ens.calibratedIndex;
	const pooled = figuresFor(ens.model.id).pooledHuman;
	let text = ci
		? STATUS_EXPLAIN[ci.status](ci.referenceIndex.toFixed(1), {
				n: thousands(pooled.n),
				flagged: String(pooled.flagged),
				bound: pooled.bound,
			})
		: STATUS_EXPLAIN['not-available']('', {
				n: thousands(pooled.n),
				flagged: String(pooled.flagged),
				bound: pooled.bound,
			});
	// CD-108: a length group with its own operating point (the short-text point) has its own sentence
	const shortPoint = ci ? shortPointSentence(ens.model.id, ci) : null;
	if (shortPoint) text = shortPoint;
	// CD-94: a length group whose calibration map cannot reach the reference point
	const unreachable = ci ? unreachableSentence(ens.model.id, ci.group, ci.status) : null;
	if (unreachable) text += ` ${unreachable}`;
	const blocks: Block[] = [
		{ t: 'p', text },
		{ t: 'p', text: HEADLINE_EXPLAIN.notLines },
	];
	// UD-36: the confidence reading of the authorship dial, with its conditions, wherever the report carries the index
	const dial = dialTextLines(ci, ens.model.id);
	if (dial.length > 0)
		blocks.push({ t: 'p', text: dial[0] as string }, { t: 'list', items: dial.slice(1) });
	if (ci && ci.index !== null && ci.index > 50 !== ens.index > 50)
		blocks.push({ t: 'p', text: HEADLINE_EXPLAIN.reconcile });
	return blocks;
}

/** P15: the channel explainers appendix (one row per channel, in registry order). */
function explainersSection(vm: ViewModel): ReportSection {
	const names = new Map<string, string>(vm.channels.map((c) => [c.id, c.name]));
	return {
		id: 'channel-explainers',
		title: 'Channel explainers',
		blocks: [
			{
				t: 'p',
				text: 'What each channel measures, why it is in the ensemble, which way is machine-like, what else can cause the same reading, and how much text it needs.',
			},
			{
				t: 'table',
				head: [
					'ID',
					'Name',
					'Measures',
					'Why it matters',
					'Direction',
					'Human causes too',
					'Needs',
				],
				rows: allExplainers().map((e) => [
					e.id,
					names.get(e.id) ?? '',
					e.measures,
					e.why,
					DIRECTION[e.direction],
					e.humanCauses,
					e.needs,
				]),
				align: ['l', 'l', 'l', 'l', 'l', 'l', 'l'],
			},
		],
	};
}

/** P15: the glossary appendix, as a term / definition list in `GLOSSARY_KEYS` order. */
function glossarySection(): ReportSection {
	return {
		id: 'glossary',
		title: 'Glossary',
		blocks: [
			{
				t: 'kv',
				rows: glossaryEntries().map(([, g]): [string, string] => [
					g.term,
					g.use ? `${g.definition} ${g.use}` : g.definition,
				]),
			},
		],
	};
}

/** Applies `clean` to every string of the report. */
function sanitise(r: Report): Report {
	const c = clean;
	const cs = (a: string[]): string[] => a.map(c);
	r.title = c(r.title);
	r.source = r.source.map(([k, v]) => [c(k), c(v)]);
	for (const s of r.sections) {
		s.title = c(s.title);
		s.blocks = s.blocks.map((b): Block => {
			switch (b.t) {
				case 'p':
					return { ...b, text: c(b.text) };
				case 'h':
					return { t: 'h', text: c(b.text) };
				case 'list':
					return { t: 'list', items: cs(b.items) };
				case 'kv':
					return { t: 'kv', rows: b.rows.map(([k, v]) => [c(k), c(v)]) };
				case 'table': {
					for (const row of b.rows)
						for (let i = 0; i < row.length; i++) row[i] = c(row[i] as string);
					return { t: 'table', head: cs(b.head), rows: b.rows, align: b.align };
				}
			}
		});
	}
	return r;
}

export function buildReport(vm: ViewModel): Report {
	const { meta, ensemble: ens } = vm;
	const speech = figuresFor(ens.model.id).speechCaveat;
	const count = makeCounter(vm);
	const sections: ReportSection[] = [
		{
			id: 'headline',
			title: 'Headline',
			blocks: [
				{ t: 'p', text: ens.headline },
				// beside the index (CD-66), as on screen and in the CLI report
				...(speech ? [{ t: 'p' as const, text: speech.plain }] : []),
				...(ens.compositeHeadline ? [{ t: 'p' as const, text: ens.compositeHeadline }] : []),
				{ t: 'p', text: ens.translationHeadline },
				...headlineHelpBlocks(vm),
			],
		},
		{ id: 'caveat', title: 'Caveat', blocks: [{ t: 'p', text: ens.caveat }] },
		patternsSection(vm),
		noticesSection(vm),
		channelsSection(vm),
		jointSection(vm, count),
		unitsSection(vm, count),
		hotspotsSection(vm),
		{ id: 'diagnostics', title: 'Diagnostics', blocks: diagBlocks(diagnosticsSections(vm)) },
		explainersSection(vm),
		glossarySection(),
	];
	return sanitise({ title: `Provenance report: ${meta.name}`, source: sourceRows(vm), sections });
}
